/**
 * The app-capability glyphs: one per marketing feature card, plus the two the
 * phone mock needs to draw the note screen as the app actually renders it. All
 * of them stand for capabilities the shipped iOS app has (FR-001,
 * contracts/web-surface.md §2). If a card goes, its glyph goes with it.
 *
 * Each card states its own claim in a heading and a line of body text, so the
 * glyph beside it repeats what has already been said. They are therefore left
 * decorative, and hidden from assistive technology — see `glyph.tsx`.
 */

import { Glyph, type GlyphProps } from "./glyph";

/** Unlimited recording with crash recovery. */
export function MicrophoneGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5.5 11v1a6.5 6.5 0 0 0 13 0v-1M12 18.5V21M8.5 21h7" />
    </Glyph>
  );
}

/** Transcript lines — words appearing as you speak. */
export function TranscriptGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M4 7h16M4 12h16M4 17h9" />
    </Glyph>
  );
}

/** Linked notes — wikilinks and backlinks. */
export function LinkGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M10.5 7H7.5a5 5 0 0 0 0 10h3M13.5 7h3a5 5 0 0 1 0 10h-3M8.5 12h7" />
    </Glyph>
  );
}

/**
 * A derived suggestion — the note screen's Related marker. Mirrors the MDI
 * `lightbulb-on-outline` the app draws there, in the same `tertiary` hue, so
 * the mock reads as the screen it is copying rather than as a second design.
 * A suggestion is not a link, which is exactly why it does not wear
 * `LinkGlyph`.
 */
export function SuggestionGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M9 14.6a4.5 4.5 0 1 1 6 0V17H9v-2.4Z" />
      <path d="M9.75 19.5h4.5M10.5 21.5h3" />
      <path d="M12 3.5V2M5.9 6.4 4.8 5.3M18.1 6.4l1.1-1.1" />
    </Glyph>
  );
}

/** Optional AI titles and tags via Claude, using the visitor's own API key. */
export function SparkleGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M12 4C12.6 8.2 15.8 11.4 20 12 15.8 12.6 12.6 15.8 12 20 11.4 15.8 8.2 12.6 4 12 8.2 11.4 11.4 8.2 12 4Z" />
    </Glyph>
  );
}

/** Read-aloud (TTS). */
export function SpeakerGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <path d="M11 4.5L6 9H3.5A1.5 1.5 0 0 0 2 10.5v3A1.5 1.5 0 0 0 3.5 15H6l5 4.5Z" />
      <path d="M14.5 9.5a3.5 3.5 0 0 1 0 5M17.5 7a7 7 0 0 1 0 10" />
    </Glyph>
  );
}

/**
 * Plain markdown + WAV in an Obsidian-openable vault in the Files app — no
 * server, no lock-in. A safe door rather than a padlock: the point is that the
 * notes are kept, not that they are shut away from their owner.
 */
export function VaultGlyph(props: GlyphProps) {
  return (
    <Glyph {...props}>
      <rect x="2.5" y="3.5" width="19" height="17" rx="3" />
      <circle cx="12" cy="12" r="4.25" />
      <path d="M12 12L15.2 8.8" />
    </Glyph>
  );
}
